import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from "react-redux";
import { fetchOwnedBrands } from "../../redux/actions/brand";
import Loader from "../Layout/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AiOutlineClose } from 'react-icons/ai';
import { adsServer } from "../../server";
import AdSetModalForBrands from './AdSetModalForBrands';

const getBrandShopId = (brand) => brand?.shopOwner?._id || brand?.shopOwner || '';
const getBrandImage = (brand) => brand?.avatarImage?.url || brand?.coverImage?.url || '';

const SponsorBrand = ({ onSave, adType, details = {}, adminMode = false, adminOptions = {} }) => {
    const zeroRange = { min: 0, max: 0 };
    const [selectedBrandId, setSelectedBrandId] = useState(details?.brandId || '');
    const [adSpendPerDay, setAdSpendPerDay] = useState(details?.adSpend || 50);
    const [adDuration, setAdDuration] = useState(details?.adDuration || 7);
    const [totalAdSpend, setTotalAdSpend] = useState(adSpendPerDay * adDuration);
    const [predictedClicks, setPredictedClicks] = useState({ min: 0, max: 0 });
    const [predictedImpressions, setPredictedImpressions] = useState({ min: 0, max: 0 });
    const [displayedPredictedClicks, setDisplayedPredictedClicks] = useState({ min: 0, max: 0 });
    const [displayedPredictedImpressions, setDisplayedPredictedImpressions] = useState({ min: 0, max: 0 });
    const { ownedBrands } = useSelector(state => state.brand);
    const brands = adminMode ? (adminOptions.brands || []) : ownedBrands;
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();
    const debounceRef = useRef(null);
    const [isAdSetModalOpen, setIsAdSetModalOpen] = useState(false);
    const [adSet, setAdSet] = useState(details?.adSet || {}); 
    const [isAdSetSaved, setIsAdSetSaved] = useState(!!details?.adSet);
    const [activeTimeFrame, setActiveTimeFrame] = useState('total');
    const [predictionMessage, setPredictionMessage] = useState('No ad prediction data available yet.');

    useEffect(() => {
        if (!adminMode) {
            dispatch(fetchOwnedBrands());
        }
    }, [dispatch, adminMode]);

    useEffect(() => {
        setIsLoading(false);
        setTotalAdSpend(adSpendPerDay * adDuration);
        fetchPredictions(adSpendPerDay * adDuration);
    }, [adSpendPerDay, adDuration]);

    const handleDeselectBrand = (event) => {
        event.stopPropagation();
        setSelectedBrandId('');
    };

    const updateDisplayValues = (mode, clicksRange = predictedClicks, impressionsRange = predictedImpressions) => {
        const factor = mode === 'daily' ? 1 / adDuration : 1;
        setDisplayedPredictedClicks({
            min: Math.round(clicksRange.min * factor),
            max: Math.round(clicksRange.max * factor)
        });
        setDisplayedPredictedImpressions({
            min: Math.round(impressionsRange.min * factor),
            max: Math.round(impressionsRange.max * factor)
        });
    };

    const fetchPredictions = async (spend) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await axios.post(`${adsServer}/sponsored/predict`, { adSpend: spend }, { withCredentials: true });
                if (data?.hasHistoricalData === false) {
                    setPredictedClicks(zeroRange);
                    setPredictedImpressions(zeroRange);
                    updateDisplayValues('total', zeroRange, zeroRange);
                    setPredictionMessage(data.message || 'No ad prediction data available yet.');
                    return;
                }

                if (data) {
                    setPredictedClicks(data.predictedClicks);
                    setPredictedImpressions(data.predictedImpressions);
                    updateDisplayValues('total', data.predictedClicks, data.predictedImpressions);
                    setPredictionMessage('');
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.response?.data || '';
                if (error.response?.status === 404 || /No historical data available/i.test(String(errorMessage))) {
                    setPredictedClicks(zeroRange);
                    setPredictedImpressions(zeroRange);
                    updateDisplayValues('total', zeroRange, zeroRange);
                    setPredictionMessage('No ad prediction data available yet.');
                    return;
                }

                console.error('Error fetching predictions:', error);
                toast.error('Failed to fetch predictions');
            }
        }, 500);
    };

    const handleDisplayModeChange = (mode) => {
        setActiveTimeFrame(mode); 
        updateDisplayValues(mode);
    };

    const openAdSetModal = () => {
        setIsAdSetModalOpen(true);
    };

    const handleAdSetSave = (newAdSet) => {
        setAdSet(newAdSet);
        setIsAdSetSaved(true);
        setIsAdSetModalOpen(false);
    };

    const handleSaveAd = () => {
        if (!selectedBrandId) {
            toast.error("Please select a brand before saving.");
            return;
        }

        const selectedBrand = brands.find(brand => brand._id === selectedBrandId);

        const details = {
            brandId: selectedBrandId,
            brandName: selectedBrand?.name,
            brandImage: getBrandImage(selectedBrand),
            shopId: getBrandShopId(selectedBrand),
            adSpend: totalAdSpend,
            adDuration,
            adSet,
        };

        onSave(adSet, details, adType);
        toast.success("Brand ad configuration saved.");
    };

    // Determine if the Save button should be enabled
    const isSaveEnabled = isAdSetSaved && selectedBrandId;

    return (
        <>
            {isLoading ? <Loader /> : (
                <div className="sponsor-brand-component-container">
                    <ToastContainer />
                    <form>
                        <h2 className="sponsor-brand-component-title">Sponsor Your Brand</h2>

                        <div className="sponsor-brand-component-product-selection">
                            <label>Select a Brand:</label>
                            <div className="sponsor-brand-component-product-list">
                                {brands.map(brand => (
                                    <div key={brand._id} 
                                         onClick={() => setSelectedBrandId(brand._id)} 
                                         className={`sponsor-brand-component-product-item ${selectedBrandId === brand._id ? 'selected' : ''}`}>
                                        <img src={getBrandImage(brand)} alt={brand.name} />
                                        <span>{brand.name}</span>
                                        {selectedBrandId === brand._id && (
                                            <AiOutlineClose 
                                                onClick={handleDeselectBrand}
                                                className="sponsor-brand-component-close-icon"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {selectedBrandId && (
                            <>
                                <div className="customize-ad-set-button-container">
                                    <button type="button" onClick={openAdSetModal} className="customize-ad-set-button">
                                        Customize Ad Set
                                    </button>
                                </div>
                            </>
                        )}

                        <div className="sponsor-product-component-slider">
                            <label>Set Ad Spend per Day ($):</label>
                            <input 
                                type="text"
                                value={adSpendPerDay}
                                onChange={(e) => {
                                    const newSpend = Number(e.target.value);
                                    setAdSpendPerDay(newSpend);
                                    fetchPredictions(newSpend * adDuration); 
                                }}
                                className="sponsor-brand-component-input"
                            />
                            <input 
                                type="range" 
                                min="1" 
                                max="1000"
                                value={adSpendPerDay}
                                onChange={(e) => {
                                    const newSpend = Number(e.target.value);
                                    setAdSpendPerDay(newSpend);
                                    fetchPredictions(newSpend * adDuration); 
                                }}
                                className="sponsor-product-component-range-slider"
                            />
                        </div>

                        <div className="sponsor-product-component-slider">
                            <label>Set Ad Duration (Days):</label>
                            <input 
                                type="text"
                                value={adDuration}
                                onChange={(e) => {
                                    const newDuration = Number(e.target.value);
                                    setAdDuration(newDuration);
                                    fetchPredictions(adSpendPerDay * newDuration); 
                                }}
                                className="sponsor-brand-component-input"
                            />
                            <input 
                                type="range" 
                                min="1" 
                                max="30"
                                value={adDuration}
                                onChange={(e) => {
                                    const newDuration = Number(e.target.value);
                                    setAdDuration(newDuration);
                                    fetchPredictions(adSpendPerDay * newDuration); 
                                }}
                                className="sponsor-product-component-range-slider"
                            />
                        </div>

                        <div className="sponsor-brand-component-total-spend">
                            <label>Total Ad Spend ($): {totalAdSpend}</label>
                        </div>

                        <div className="sponsor-product-component-display-mode">
                        <button
                            type="button"
                            onClick={() => handleDisplayModeChange('daily')}
                            className={`time-button ${activeTimeFrame === 'daily' ? 'active-time-button' : ''}`}
                        >
                            Daily
                        </button>
                        <button
                            type="button"
                            onClick={() => handleDisplayModeChange('total')}
                            className={`time-button ${activeTimeFrame === 'total' ? 'active-time-button' : ''}`}
                        >
                            Total
                        </button>
                    </div>

                        <div className="sponsor-brand-component-predictions">
                            {displayedPredictedClicks.min !== 0 || displayedPredictedClicks.max !== 0 ? (
                                <>
                                    <div className="sponsor-brand-component-prediction-item">
                                        <label>Predicted Clicks Range:</label>
                                        <span>{displayedPredictedClicks.min} to {displayedPredictedClicks.max}</span>
                                    </div>
                                    <div className="sponsor-brand-component-prediction-item">
                                        <label>Predicted Impressions Range:</label>
                                        <span>{displayedPredictedImpressions.min} to {displayedPredictedImpressions.max}</span>
                                    </div>
                                </>
                            ) : (
                                <div className="sponsor-brand-component-null-message">
                                    {predictionMessage}
                                </div>
                            )}
                        </div>

                        <button 
                            type="button" 
                            className={`sponsor-brand-component-submit-button ${isSaveEnabled ? 'enabled' : 'disabled'}`} 
                            onClick={isSaveEnabled ? handleSaveAd : null} 
                            disabled={!isSaveEnabled}
                        >
                            Save Ad Configuration
                        </button>
                    </form>

                    <AdSetModalForBrands
                        key={selectedBrandId}
                        isOpen={isAdSetModalOpen}
                        onClose={() => setIsAdSetModalOpen(false)}
                        onSave={handleAdSetSave}
                        adFormats={['Top Banner Ad', 'Top Banner Mobile Ad', 'Banner Ad', 'Banner Mobile Ad', 'Sponsored Brands', 'Sponsored Brands Carousel', 'Sponsored Brands Carousel Mobile', 'Dual Carousel', 'Quad Carousel', 'Sponsored Feed Ads']}
                        brandImage={getBrandImage(brands.find(brand => brand._id === selectedBrandId))}
                        brandName={brands.find(brand => brand._id === selectedBrandId)?.name}
                        brandDescription={brands.find(brand => brand._id === selectedBrandId)?.description}
                    />
                </div>
            )}
        </>
    );
};

export default SponsorBrand;


